import { Injectable } from '@angular/core';
import { ErrorResponseHandler } from './error-response-handler';
import { HttpErrorResponse } from '@angular/common/http';
import { DataIntegrityProblemDetails } from '../../models/error-responses/data-integrity-problem-details.model';
import { BaseErrorResponseHandler } from './base-error-response-handler';
import { DataIntegrityField } from '../../models/error-responses/data-integrity-field.model';
import { ProblemDetails } from '../../models/error-responses/problem-details.model';

@Injectable()
export class DataIntegrityErrorResponseHandler extends BaseErrorResponseHandler implements ErrorResponseHandler {
  priority = 100;

  canHandle(error: HttpErrorResponse): boolean {
    if (error.status === 409 && this.isDataIntegrityProblemDetails(error.error)) {
      return true;
    }

    return false;
  }

  isDataIntegrityProblemDetails(problem: unknown): problem is DataIntegrityProblemDetails {
    if (!problem || typeof problem !== 'object') {
      return false;
    }

    if (!('violation' in problem)) {
      return false;
    }

    const { violation } = problem as { violation?: unknown };
    return violation === null || violation === undefined || typeof violation === 'object';
  }

  buildErrorMessages(error: HttpErrorResponse): string[] {
    const problem: DataIntegrityProblemDetails = error.error as DataIntegrityProblemDetails;
    const fields: DataIntegrityField[] | undefined = problem.violation?.fields;
    const fallback: string = this.getDefaultErrorMessage(error.error);
    if (fields && fields.length > 0) {
      const messages: string[] = fields.map((field) => {
        const column: string | undefined = field.column;
        const attemptedValue: string | undefined = field.attemptedValue;
        if (column && attemptedValue) {
          return `${column} "${attemptedValue}" is already in use.`;
        } else {
          return fallback;
        }
      });

      return Array.from(new Set(messages.filter(Boolean)));
    }

    return [fallback];
  }

  getDefaultErrorMessage(problem: ProblemDetails | undefined): string {
    const baseErrorMessage: string = this.baseErrorMessage(problem);
    if (baseErrorMessage && baseErrorMessage.trim() !== '') {
      return baseErrorMessage;
    }

    return this.defaultErrorMessage();
  }

  defaultErrorMessage(): string {
    return 'Conflict detected. Please resolve and try again.';
  }
}
